import { Anchor, Box, Text, ThemeContext } from "grommet";
import React, { useContext } from "react";
import useSWR from "swr";
import { silentFetcher } from "../utils/errors";
import { useRouter } from "next/router";
import FontAwesomeIcon from "./FontAwesomeIcon";
import {
    faUserGroup as IconGroup,
    faXmarkLarge as IconClose,
    faUsers as IconMembers
} from "@fortawesome/pro-light-svg-icons";
import { NAME_CARD_MIN_WIDTH } from "../constants/Dimensions";
import { DarkenOnHoverBox, DisplayInlineBlockBox, EnhancedDropButton } from "../utils/styled";
import Button from "./Button";
import ContentLine from "./ContentLine";

const GroupTag = ({ id, onRemove = undefined, inlineBlock = true, inline = false }) => {
    const router = useRouter();
    const theme = useContext(ThemeContext);

    const { data: groupData } = useSWR(
        id ? `/api/customers/groups/${id}/details` : undefined,
        silentFetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000, // 1 minute - reduce duplicate requests
        }
    );

    const groupName = groupData?.company_name || groupData?.name;
    const memberCount = groupData?.customers?.length || 0;

    const TagWrapper = ({ inlineBlock, children }) => {
        return inlineBlock ? (
            <DisplayInlineBlockBox>{children}</DisplayInlineBlockBox>
        ) : (
            children
        );
    };

    return (
        <TagWrapper inlineBlock={inlineBlock}>
            <EnhancedDropButton
                event={{ section: 'group_tag', element: 'tag', description: groupData?.uuid }}
                styles={'max-width: 100%;'}
                label={(
                    <DarkenOnHoverBox
                        background={theme.dark ? "foreground" : "light-1"}
                        border={{ color: "border" }}
                        round="xxsmall"
                        theme={theme}
                    >
                        <Box direction="row" align="center" pad="xxsmall" justify={!inlineBlock ? "between" : undefined}>
                            <Box pad={{ horizontal: 'xxsmall' }} direction="row" align="center" gap="xsmall">
                                <FontAwesomeIcon SelectedIcon={IconGroup} size="xsmall" color="text-weak" />
                                <Text truncate wordBreak="keep-all">
                                    {groupName || ''}
                                </Text>
                                {!groupData && (<ContentLine width="xsmall" isLoading={true} />)}
                            </Box>
                            {onRemove && (
                                <Box pad={{ right: 'xsmall' }}>
                                    <Anchor
                                        plain
                                        hoverIndicator
                                        label={(<FontAwesomeIcon SelectedIcon={IconClose} color="text-weak" size="xxxsmall" />)}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemove(id);
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </DarkenOnHoverBox>
                )}
                plain
                hoverIndicator
                dropAlign={{ top: 'bottom', left: 'left' }}
                dropProps={{ border: true, margin: { vertical: 'xsmall' }, inline }}
                dropContent={groupData ? (
                    <Box pad="small" margin="small" width={{ max: 'medium' }} flex={{ shrink: 0 }}>
                        <Box margin={{ vertical: 'xsmall' }} border={{ side: 'bottom' }} pad={{ bottom: 'small' }} width={{ min: NAME_CARD_MIN_WIDTH }}>
                            <Box direction="row" align="center" gap="xsmall">
                                <FontAwesomeIcon SelectedIcon={IconGroup} size="small" color="text-weak" />
                                <Text size="large" weight={500}>
                                    {groupName}
                                </Text>
                            </Box>
                        </Box>
                        <Box direction="column" gap="small" pad={{ vertical: 'small' }}>
                            <Box direction="row" align="center" gap="small">
                                <FontAwesomeIcon SelectedIcon={IconMembers} size="xsmall" />
                                <Text>{memberCount} member{memberCount !== 1 ? 's' : ''}</Text>
                            </Box>
                        </Box>
                        <Box margin={{ top: 'small' }}>
                            <Button
                                event={{ section: 'group_tag', element: 'view_profile', description: groupData?.uuid }}
                                primary
                                ctaColor="accent"
                                color="accent"
                                fill="horizontal"
                                label={(
                                    <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                        <Text color="text">View profile</Text>
                                    </Box>
                                )}
                                onClick={() => router.push(`/customers/groups/${groupData?.uuid}`)}
                            />
                        </Box>
                    </Box>
                ) : (<></>)}
                width={{ max: '100%' }}
                fill={!inlineBlock ? "horizontal" : undefined}
            />
        </TagWrapper>
    );
};

export default GroupTag;
